import React, { useState, useEffect } from 'react';
import { Button, Dropdown, MenuProps } from 'antd';
import {
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  StopOutlined,
  SwapOutlined,
  HistoryOutlined,
  MoreOutlined,
  MergeCellsOutlined,
  SplitCellsOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { Table, TableStatus } from '../../types/tables';
import { useCanDeleteTableQuery } from '../../services/validationApi';
import { useAppSelector } from '../../hooks/redux';
import TableHistory from './TableHistory';
import { isAdmin as checkIsAdmin } from '../../utils/roleHelpers';
import {
  statusClassMap,
  statusLabelMap,
  formatElapsedTime,
  formatMoneyCompact,
  isStaleTable,
} from './tableHelpers';

interface TableCardProps {
  table: Table;
  onEdit?: (table: Table) => void;
  onDelete?: (tableId: string) => void;
  onOpen?: (table: Table) => void;
  onClose?: (tableId: string) => void;
  onTransfer?: (table: Table) => void;
  onMerge?: (table: Table) => void;
  onSplit?: (table: Table) => void;
  onStatusChange?: (tableId: string, status: TableStatus) => void;
  onClick?: (table: Table) => void;
  isSelected?: boolean;
  showAreaName?: boolean;
}

const TableCard: React.FC<TableCardProps> = ({
  table,
  onEdit,
  onDelete,
  onOpen,
  onTransfer,
  onMerge,
  onSplit,
  onStatusChange,
  onClick,
  isSelected = false,
  showAreaName = false,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (table.status !== 'Occupied' || !table.lastActivity) return;
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, [table.status, table.lastActivity]);

  const isAdminUser = checkIsAdmin(user?.role);
  // Only admins see the delete menu item — don't fire one validation request
  // per card for everyone else (30 tables = 30 requests otherwise).
  const { data: canDeleteData } = useCanDeleteTableQuery(table.id, { skip: !isAdminUser });
  const canDelete = canDeleteData?.canDelete ?? true;
  const statusClass = statusClassMap[table.status] || 'available';

  const hasOpenOrder = table.status === 'Occupied' || table.status === 'PreCheck';
  const stale = isStaleTable(table);

  // Merged-tables state: a secondary is joined to a primary and fully locked
  // (its order lives on the primary); a primary carries the group's order.
  const isMergedSecondary = !!table.mergedIntoTableId;
  const isMergedPrimary = (table.mergedTables?.length ?? 0) > 0;
  const canMerge = !isMergedSecondary &&
    (table.status === 'Available' || table.status === 'Reserved' || table.status === 'Occupied');

  const menuItems: MenuProps['items'] = [
    {
      key: 'history',
      label: 'Historial de Mesa',
      icon: <HistoryOutlined />,
      onClick: () => setHistoryModalVisible(true),
    },
    ...(table.status === 'Available' && !isMergedSecondary ? [{
      key: 'reserve',
      label: 'Reservar Mesa',
      icon: <SwapOutlined />,
      onClick: () => onStatusChange?.(table.id, 'Reserved'),
    }] : []),
    ...(table.status === 'Reserved' && !isMergedSecondary ? [{
      key: 'cancel-reservation',
      label: 'Cancelar Reserva',
      icon: <StopOutlined />,
      onClick: () => onStatusChange?.(table.id, 'Available'),
    }] : []),
    ...(hasOpenOrder && !isMergedSecondary && !isMergedPrimary ? [{
      key: 'transfer',
      label: 'Transferir Mesa',
      icon: <SwapOutlined />,
      onClick: () => onTransfer?.(table),
    }] : []),
    ...(canMerge ? [{
      key: 'merge',
      label: isMergedPrimary ? 'Unir Otra Mesa' : 'Unir Mesas',
      icon: <MergeCellsOutlined />,
      onClick: () => onMerge?.(table),
    }] : []),
    ...(isMergedPrimary ? [{
      key: 'split',
      label: 'Separar Mesas',
      icon: <SplitCellsOutlined />,
      onClick: () => onSplit?.(table),
    }] : []),
    ...(isAdminUser ? [
      { type: 'divider' as const },
      {
        key: 'edit',
        label: 'Editar Mesa',
        icon: <EditOutlined />,
        onClick: () => onEdit?.(table),
      },
      ...(table.status === 'Dirty' ? [{
        key: 'available',
        label: 'Liberar Mesa',
        icon: <PlayCircleOutlined />,
        onClick: () => onStatusChange?.(table.id, 'Available'),
      }] : []),
      { type: 'divider' as const },
      {
        key: 'delete',
        label: 'Eliminar Mesa',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: !canDelete || hasOpenOrder || isMergedSecondary || isMergedPrimary,
        onClick: () => canDelete && onDelete?.(table.id),
      },
    ] : []),
  ];

  const handleTableClick = () => {
    // A secondary always redirects to its group: the parent handler resolves
    // the primary and opens its management view.
    if (isMergedSecondary) {
      onClick?.(table);
      return;
    }
    if (table.status === 'Available' || table.status === 'Reserved') {
      onOpen?.(table);
    } else {
      onClick?.(table);
    }
  };

  // Badge: status label, plus the running time for occupied tables.
  // Secondaries show where the group lives instead of a bare "Ocupada".
  const badgeContent = isMergedSecondary
    ? `Unida a Mesa ${table.mergedIntoTableNumber}`
    : table.status === 'Occupied'
      ? `${statusLabelMap.Occupied} · ${formatElapsedTime(table.lastActivity)}`
      : statusLabelMap[table.status];

  // A merged primary counts the whole group's seats
  const totalCapacity = isMergedPrimary
    ? table.capacity + (table.mergedTables?.reduce((sum, t) => sum + t.capacity, 0) ?? 0)
    : table.capacity;

  // Guests: "3/4" while there's an open order, plain capacity otherwise
  const capacityContent = hasOpenOrder && table.guestCount
    ? `${table.guestCount}/${totalCapacity}`
    : `${totalCapacity}`;

  // Los nombres largos bajan de cuerpo en escalones; el ellipsis del CSS
  // remata lo que ni así quepa. Los umbrales son por conteo de caracteres
  // porque el ancho de la tarjeta es estable dentro de cada breakpoint.
  const numberSizeClass =
    table.number.length > 10
      ? ' table-card-number--sm'
      : table.number.length > 4
        ? ' table-card-number--md'
        : '';

  const renderFooter = () => {
    if (isMergedSecondary) {
      return <div className="table-card-hint">Cuenta en Mesa {table.mergedIntoTableNumber}</div>;
    }
    if (stale) {
      return (
        <div className="table-card-alert">
          ¿Olvidada?
          <span className="table-card-alert-action">Revisar</span>
        </div>
      );
    }
    if (hasOpenOrder) {
      return (
        <div className="table-card-meta">
          <span className="table-card-waiter">{table.assignedWaiterName || '—'}</span>
          {table.currentOrderTotal != null && (
            <span className="table-card-total">{formatMoneyCompact(table.currentOrderTotal)}</span>
          )}
        </div>
      );
    }
    if (table.status === 'Dirty') {
      return (
        <Button
          type="primary"
          size="small"
          icon={<PlayCircleOutlined />}
          className="table-card-liberar"
          onClick={(e) => {
            e.stopPropagation();
            onStatusChange?.(table.id, 'Available');
          }}
        >
          Liberar
        </Button>
      );
    }
    if (table.status === 'Available') {
      return <div className="table-card-hint">Toca para abrir</div>;
    }
    return null;
  };

  return (
    <>
      <div
        className={`table-card tcard-${statusClass}${stale ? ' tcard-stale' : ''}${isSelected ? ' selected' : ''}`}
        onClick={handleTableClick}
      >
        {/* Area as an eyebrow above the number (only in the "Todas" view) — full width,
            never competes with the badge for space */}
        {showAreaName && table.areaName && (
          <div className="table-card-eyebrow">{table.areaName}</div>
        )}

        <div className="table-card-top">
          <div className={`table-card-number${numberSizeClass}`} title={table.number}>
            {table.number}
          </div>
          {/* Guests + menu live in the flow (no absolute positioning) so nothing overlaps */}
          <div className="table-card-top-right" onClick={(e) => e.stopPropagation()}>
            <span className="table-card-guests">
              <UserOutlined /> {capacityContent}
            </span>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <button
                className="table-card-menu-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreOutlined />
              </button>
            </Dropdown>
          </div>
        </div>

        <span className={`table-card-badge ${statusClass}`}>
          {isMergedSecondary && <LinkOutlined style={{ marginRight: 4 }} />}
          {badgeContent}
        </span>

        {isMergedPrimary && (
          <div className="table-card-merged">
            <LinkOutlined /> Con Mesa {table.mergedTables!.map(t => t.number).join(', ')}
          </div>
        )}

        {renderFooter()}
      </div>

      <TableHistory
        visible={historyModalVisible}
        tableId={table.id}
        tableName={`Mesa ${table.number}`}
        onClose={() => setHistoryModalVisible(false)}
      />
    </>
  );
};

export default TableCard;
